'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { gsap } from 'gsap';
import { Icon, useResizeObserver, useTranslation, useUi } from '@weareconceptstudio/core';
import HeaderStyle from './style';
import { config } from '@/helpers';
import CustomLink from '../CustomLink';
import MenuBar from './MenuBar';
import LanguageSwitcher from './LanguageSwitcher';
import BurgerMenuIcon from './BurgerIcon';
import { withDataContext } from '@/context';
import Button from '../Button';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Logo from '../Logo';

gsap.registerPlugin(ScrollTrigger);

const Header = ({ setFirstLoad, firstLoad }) => {
	const pathname = usePathname();
	const { selectedLang } = useTranslation();
	const { winWidth, isBurgerMenuOpen, closeBurgerMenu } = useUi();

	const headerRef = useRef();
	const oldScroll = useRef(0);
	const refScrollTrigger = useRef();

	const [show, setShow] = useState(true);

	// const [isHeaderHaveBackground, setIsHeaderHaveBackground] = useState(false);

	const cleanedPathname = useMemo(() => {
		if (!selectedLang) {
			return pathname || '/';
		}

		const withoutLang = pathname.replaceAll(`/${selectedLang}`, '');

		return withoutLang === '' ? '/' : withoutLang;
	}, [pathname, selectedLang]);

	const isMobileScreen = useMemo(() => {
		return winWidth < 1280;
	}, [winWidth]);

	const isIncentivesPage = useMemo(() => {
		return cleanedPathname.includes('/business-environment/incentives/');
	}, [cleanedPathname]);

	const isTransparentBackground = useMemo(() => {
		return Object.values(config.routes).some((route) => route.isTransparentBackground && cleanedPathname == route.path) || isIncentivesPage;
	}, [cleanedPathname, isIncentivesPage, pathname]);

	const isBlackBackground = useMemo(() => {
		return Object.values(config.routes).some((route) => route.isBlackBackground && cleanedPathname == route.path);
	}, [cleanedPathname, pathname]);

	const onScroll = useCallback(() => {
		const scrollY = window.scrollY || 0;
		const viewportHeight = window.innerHeight || 0;

		// Always show at the very top
		if (scrollY === 0) {
			setShow(true);
		} else if (scrollY > 0 && scrollY < viewportHeight && !isBurgerMenuOpen) {
			// Between 0 and 1 viewport height: always hide (when burger menu is closed)
			setShow(false);
		} else {
			// After first viewport: behave as before
			if (scrollY > oldScroll.current && !isBurgerMenuOpen) {
				setShow(false);
			} else if (scrollY < oldScroll.current || isBurgerMenuOpen) {
				setShow(true);
			}
		}

		// window.scrollY > headerRef.current?.offsetHeight ? setIsHeaderHaveBackground(true) : setIsHeaderHaveBackground(false);

		oldScroll.current = scrollY;
		// }, [isBurgerMenuOpen, pathname, selectedLang, isHeaderHaveBackground]);
	}, [isBurgerMenuOpen, pathname, selectedLang]);

	useEffect(() => {
		closeBurgerMenu();
	}, [pathname, selectedLang]);

	useEffect(() => {
		if (isBurgerMenuOpen && !isMobileScreen) {
			closeBurgerMenu();
		}
	}, [isMobileScreen, isBurgerMenuOpen]);

	useEffect(() => {
		isBurgerMenuOpen ? document.body.classList.add('hidden') : document.body.classList.remove('hidden');
	}, [isBurgerMenuOpen, pathname, selectedLang]);

	useEffect(() => {
		window.addEventListener('scroll', onScroll);

		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	}, [isBurgerMenuOpen, pathname, selectedLang]);

	useEffect(() => {
		onScroll();
	}, [pathname, selectedLang, onScroll]);

	useEffect(() => {
		gsap.killTweensOf(headerRef.current);

		gsap.to(headerRef.current, {
			duration: window.scrollY === 0 ? 0.7 : 0.3,
			y: show ? '0%' : '-100%',
		});
	}, [show]);

	//! For Header First Load & Rerender cases
	useEffect(() => {
		if (firstLoad) {
			gsap.killTweensOf(headerRef.current);
			setFirstLoad();

			gsap.fromTo(
				headerRef.current,
				{ y: '-100%' },
				{
					duration: 0.7,
					y: '0%',
					delay: 0.3,
					ease: 'power2.out',
				}
			);
		}
	}, [pathname, selectedLang]);

	//! Header background variant
	useEffect(() => {
		// Clear any previous background state when route/language changes
		headerRef.current?.classList?.remove('background-variant');

		const hero = document.querySelector('#hero-section');

		if (hero) {
			refScrollTrigger.current = ScrollTrigger.create({
				start: `top+=${hero.clientHeight - headerRef.current.offsetHeight} top`,
				end: `bottom top`,
				// start: `top top`,
				// end: 'bottom top',
				trigger: hero,
				// markers: true,
				onEnter: () => {
					headerRef.current?.classList?.add('background-variant');
				},
				onLeaveBack: () => {
					headerRef.current?.classList?.remove('background-variant');
				},
			});
		} else {
			refScrollTrigger.current = ScrollTrigger.create({
				start: `top+=${window.innerHeight - headerRef.current.offsetHeight} top`,
				end: `bottom top`,
				// start: `top top`,
				// end: 'bottom top',
				trigger: document.body,
				// markers: true,
				onEnter: () => {
					headerRef.current?.classList?.add('background-variant');
				},
				onLeaveBack: () => {
					headerRef.current?.classList?.remove('background-variant');
				},
			});
		}

		return () => {
			refScrollTrigger.current && refScrollTrigger.current.kill();
		};
	}, [pathname, selectedLang]);

	return (
		<HeaderStyle
			ref={headerRef}
			$firstLoad={firstLoad}
			// className={`${isHeaderHaveBackground || isBurgerMenuOpen ? 'background-variant' : ''} ${isTransparentBackground ? 'transparent-variant' : ''}`}
			className={`${isBurgerMenuOpen ? 'background-variant' : ''} ${isTransparentBackground ? 'transparent-variant' : ''} ${isBlackBackground ? 'black-variant' : ''}`}>
			<div className='header-inner-wrapper'>
				<div className='header-logo-block'>
					<CustomLink
						aria-label='Home'
						url={config.routes.home.path}>
						<Logo color={'var(--red1000)'} />
					</CustomLink>
				</div>

				<MenuBar />

				<div className='header-right-block'>
					<div className='contact-btn-block'>
						<Button
							btnType={'secondary-transparent'}
							url={config.routes.contactUs.path}
							text={config.routes.contactUs.name}
						/>
					</div>

					<LanguageSwitcher />
					<CustomLink
						url={config.routes.search.path}
						className='search-icon-wrapper'>
						<Icon
							name={'search'}
							className='search-icon black-1000 cursor-pointer'
						/>
					</CustomLink>

					{isMobileScreen ? <BurgerMenuIcon /> : null}
				</div>
			</div>
		</HeaderStyle>
	);
};

export default withDataContext(Header, ['setFirstLoad', 'firstLoad']);
